import { Alert, AlertProps, Button, Card, Divider, Flex, Popconfirm, Typography, theme } from 'antd';
import { FC, ReactElement, memo } from 'react';
import { CheckCircleFill, ExclamationCircleFill, PlayCircleFill, XCircleFill } from 'react-bootstrap-icons';

import { DomainPunycode } from '@/common/components';
import { getDateFactory } from '@/common/utils';

import { SearchConsoleAddressChangingStatus } from '@/modules/Domains';
import { Site } from '@/modules/Sites';

import { AddressChangerBadgeAlertOrCard } from './AddressChangerBadgeAlertOrCard';

export const AddressChangerBadgeAlert: FC<{
  alertType: AlertProps['type'];
  redirectingInfo: Site['searchConsoleAddressChanging'] | undefined;
  dropdownButton: ReactElement;
  logsButton: ReactElement;
  isAddingRedirectingTaskLoading: boolean;
  handleAddRedirectingTask: () => void;
}> = memo(({
  alertType,
  redirectingInfo,
  dropdownButton,
  logsButton,
  isAddingRedirectingTaskLoading,
  handleAddRedirectingTask
}) => {
  const {
    token: { colorSuccess, colorError, colorPrimary }
  } = theme.useToken();

  if (!redirectingInfo) return <></>;

  const {
    changingError,
    changedAt,
    changingStatus,
    currentChangedMainDomain,
    lastChangedMainDomain,
  } = redirectingInfo;

  const runAction = (
    <Popconfirm
      destroyTooltipOnHide
      title="Почати процес переадресації"
      icon={ <ExclamationCircleFill color={ colorPrimary }/> }
      description={ <>Ви дійсно хочете почати процес<br/>переадресації?</> }
      onConfirm={ handleAddRedirectingTask }
      okText="Так, почати"
      cancelText="Ні"
      placement="bottom"
      disabled={ isAddingRedirectingTaskLoading }
    >
      <Button
        type="link"
        size="small"
        icon={ <PlayCircleFill/> }
        loading={ isAddingRedirectingTaskLoading }
        disabled={ isAddingRedirectingTaskLoading }
      />
    </Popconfirm>
  );

  if (changingStatus === SearchConsoleAddressChangingStatus.None) {
    return (
      <AddressChangerBadgeAlertOrCard
        type={ alertType }
        content={
          <Flex justify="center" align="center" gap={ 4 }>
            { runAction }
            <Divider type="vertical" style={{ marginLeft: 0, marginRight: 6 }}/>
            <Typography.Text type="secondary">Перенаправленнь ще не було</Typography.Text>
            { dropdownButton }
          </Flex>
        }
      />
    );
  }

  if (changingStatus === SearchConsoleAddressChangingStatus.Pending) {
    return (
      <Alert
        style={{ paddingBlock: 4 }}
        type={ alertType }
        message={
          <Flex justify="center" align="center" gap={ 4 }>
            <Typography.Text type="secondary">Зміна адреси на</Typography.Text>
            <Typography.Text>
              <DomainPunycode domainName={ currentChangedMainDomain?.siteUrl ?? '' }/>
            </Typography.Text>
            <Typography.Text type="secondary">в очікуванні...</Typography.Text>
          </Flex>
        }
      />
    );
  }

  if (changingStatus === SearchConsoleAddressChangingStatus.Redirecting) {
    return (
      <Alert
        style={{ paddingBlock: 4 }}
        type={ alertType }
        message={
          <Flex justify="center" align="center" gap={ 4 }>
            <Typography.Text type="secondary">Зміна адреси на</Typography.Text>
            <Typography.Text>
              <DomainPunycode domainName={ currentChangedMainDomain?.siteUrl || '' }/>
            </Typography.Text>
            <Typography.Text type="secondary">
              в <span style={{ clipPath: 'inset(0 1ch 0 0)', animation: 'three-dots-loading 1200ms steps(4) infinite' }}>процесі...</span>
            </Typography.Text>
          </Flex>
        }
      />
    );
  }

  if (changingStatus === SearchConsoleAddressChangingStatus.Success) {
    return (
      <Card size="small" styles={{ body: { paddingBlock: 4 } }}>
        <Flex justify="center" align="center" gap={ 4 }>
          { runAction }
          <Divider type="vertical" style={{ marginLeft: 0, marginRight: 6 }}/>
          <CheckCircleFill size={ 16 } color={ colorSuccess }/>
          <Typography.Text type="secondary">Востаннє було</Typography.Text>
          <Typography.Text>{ changedAt && getDateFactory(changedAt, '08/16/2018 08:02') }</Typography.Text>
          <Typography.Text type="secondary">на</Typography.Text>
          <Typography.Text>
            <DomainPunycode domainName={ lastChangedMainDomain?.siteUrl ?? '' }/>
          </Typography.Text>
          { logsButton }
          { dropdownButton }
        </Flex>
      </Card>
    );
  }

  if (changingStatus === SearchConsoleAddressChangingStatus.Fail) {
    return (
      <AddressChangerBadgeAlertOrCard
        type={ alertType }
        content={
          <Flex justify="center" align="center" gap={ 4 }>
            { runAction }
            <Divider type="vertical" style={{ marginLeft: 0, marginRight: 6 }}/>
            <XCircleFill color={ colorError }/>
            <Typography.Paragraph
              style={{ marginBottom: 0, maxWidth: 300, color: 'inherit' }}
              ellipsis={{
                rows: 1,
                tooltip: {
                  placement: 'bottom',
                  overlayStyle: { minWidth: 'max-content' }
                }
              }}
            >
              { changingError }
            </Typography.Paragraph>
            { logsButton }
            { dropdownButton }
          </Flex>
        }
      />
    );
  }
});
